'use client'

import * as React from 'react'
import { useEffect, useMemo } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { RegisterOptions } from 'react-hook-form'

import { cn } from '../lib/utils'
import { Button } from '../shad/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '../shad/command'
import { Popover, PopoverContent, PopoverTrigger } from '../shad/popover'

interface Option {
  value: string
  label: string
}

export interface ComboboxFieldProps
  extends React.HTMLAttributes<HTMLDivElement> {
  name: string
  formConsts?: {
    errors?: {
      [key: string]: any
    }
    register: any
    getValues: any
  }
  registerOptions?: RegisterOptions
  initialValue?: string

  options: Option[]
  placeholder?: string
  searchPlaceholder?: string
  searchEmpty?: string
}

export const ComboboxField = ({
  name,
  formConsts,
  registerOptions = {},
  initialValue = '',
  options,

  placeholder = 'Select Option...',
  searchPlaceholder = 'Search options...',
  searchEmpty = 'No options found.',
}: ComboboxFieldProps) => {
  const [open, setOpen] = React.useState(false)
  const [value, setValue] = React.useState(initialValue)

  const selectedValue = formConsts?.getValues(name)

  const field = useMemo(() => {
    return { ...formConsts?.register(name, registerOptions) }
  }, [formConsts, name, registerOptions])

  useEffect(() => {
    field.onChange({ target: { name, value: initialValue } })
  }, [name])

  useEffect(() => {
    if (selectedValue) {
      setValue(selectedValue)
    }
  }, [selectedValue])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role={'combobox'}
          aria-expanded={open}
          className={cn('w-full justify-between')}
        >
          {value
            ? options.find((option) => option.value === value)?.label
            : placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0">
        <Command {...formConsts?.register(name, registerOptions)}>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandEmpty>{searchEmpty}</CommandEmpty>
          <CommandGroup className="h-[200px]">
            {options.map((option) => (
              <CommandItem
                key={option.value}
                value={option.label}
                onSelect={(currentValue) => {
                  setValue(currentValue === value ? currentValue : option.value)
                  field.onChange({ target: { name, value: option.value } })
                  setOpen(false)
                }}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    field.value === option.value ? 'opacity-100' : 'opacity-0',
                  )}
                />
                {option.label}
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
